/**
 * Study Mode Sidebar component
 * Claude-inspired sidebar with icon-only default, text on hover
 */

import {Link, useLocation} from 'react-router-dom';
import {useAuthStore} from '@voxapilot/shared';
import {api} from '../../config/firebase';
import {ROUTES} from '../../config/constants';
import {cn} from '../../utils/cn';
import {useState, useRef, useEffect} from 'react';

// SVG Icons
const DocumentsIcon = ({className}: {className?: string}) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const ChatIcon = ({className}: {className?: string}) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const FlashcardsIcon = ({className}: {className?: string}) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const AnalyticsIcon = ({className}: {className?: string}) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const navigation = [
  {name: 'Documents', href: ROUTES.STUDY_DOCUMENTS, icon: DocumentsIcon},
  {name: 'Chat', href: ROUTES.STUDY_CHAT, icon: ChatIcon},
  {name: 'Flashcards', href: ROUTES.STUDY_FLASHCARDS, icon: FlashcardsIcon},
  {name: 'Analytics', href: ROUTES.STUDY_ANALYTICS, icon: AnalyticsIcon},
];

export function StudySidebar() {
  const location = useLocation();
  const {user, signOut} = useAuthStore();
  const [isHovered, setIsHovered] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleSignOut = async () => {
    if (isSigningOut) return; // Prevent double-clicks
    
    try {
      setIsSigningOut(true);
      await signOut(api.session);
      // Navigation will happen via ProtectedRoute when isAuthenticated becomes false
    } catch (error) {
      console.error('Sign out error:', error);
      // Still set signing out to false so user can try again
      setIsSigningOut(false);
    }
  };

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 200); // Small delay before closing
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  const showText = isHovered;

  return (
    <aside
      ref={sidebarRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        'bg-[#F5F5F5] dark:bg-[#2D2D30] border-r border-gray-200 dark:border-gray-700',
        'flex flex-col h-full relative flex-shrink-0',
        'transition-all duration-200 ease-out',
        showText ? 'w-64' : 'w-16'
      )}
    >
      {/* Top Section - Navigation */}
      <div className="flex-1 overflow-y-auto py-3">
        <nav className="px-2 space-y-1">
          {navigation.map((item) => {
            const isActive =
              location.pathname === item.href ||
              (item.href === ROUTES.STUDY_DOCUMENTS && location.pathname.startsWith('/study/app/documents'));

            return (
              <Link
                key={item.name}
                to={item.href}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-md',
                  'text-sm font-medium relative',
                  'transition-all duration-150',
                  isActive
                    ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#3E3E42]'
                )}
                title={!showText ? item.name : undefined}
              >
                <item.icon className={cn(
                  'w-5 h-5 flex-shrink-0',
                  isActive ? 'text-orange-700 dark:text-orange-400' : 'text-gray-600 dark:text-gray-400'
                )} />
                {showText && (
                  <span className="truncate animate-in fade-in duration-150">{item.name}</span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {/* Bottom Section - User & Sign Out - Always at bottom */}
      <div className="mt-auto border-t border-gray-200 dark:border-gray-700 p-3 space-y-2">
        {user ? (
          <>
            {/* User Avatar - Always visible, details only on hover */}
            <div className={cn('px-3 py-2', !showText && 'flex justify-center')}>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                  {user.displayName?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || 'U'}
                </div>
                {showText && (
                  <div className="flex-1 min-w-0 animate-in fade-in duration-150">
                    <div className="text-sm font-medium text-textPrimary dark:text-[#CCCCCC] truncate">
                      {user.displayName || user.email?.split('@')[0] || 'User'}
                    </div>
                    <div className="text-xs text-textTertiary dark:text-[#6A6A6A]">
                      Free plan
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Sign Out Button */}
            <button
              onClick={handleSignOut}
              disabled={isSigningOut}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-md',
                'text-sm font-medium text-red-600 dark:text-red-400',
                'hover:bg-red-50 dark:hover:bg-red-900/20',
                'transition-colors duration-150',
                !showText && 'justify-center',
                isSigningOut && 'opacity-50 cursor-not-allowed'
              )}
              title={!showText ? (isSigningOut ? 'Signing out...' : 'Sign out') : undefined}
            >
              <svg
                className="w-5 h-5 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                />
              </svg>
              {showText && (
                <span className="animate-in fade-in duration-150">
                  {isSigningOut ? 'Signing out...' : 'Sign out'}
                </span>
              )}
            </button>
          </>
        ) : (
          // Show placeholder when not logged in (for development/testing)
          <div className={cn('px-3 py-2', !showText && 'flex justify-center')}>
            <div className="w-8 h-8 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center text-gray-600 dark:text-gray-400 text-sm font-semibold flex-shrink-0">
              ?
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

